import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, Zap, Phone, CheckCircle, AlertTriangle, Battery, XCircle, Info } from 'lucide-react';
import useSEO from '../../hooks/useSEO';

const ComoSaberBateriaMuerta = () => {
  const navigate = useNavigate();

  useSEO({
    title: 'Cómo Saber si la Batería del Coche Está Muerta | Síntomas | ArrancaYa',
    description: '¿Tu coche no arranca? Aprende a identificar si tu batería está muerta o simplemente descargada. Síntomas claros y qué hacer en cada caso. Servicio 24/7 Barcelona.',
    canonical: 'https://arrancaya-bcn.es/blog/como-saber-bateria-muerta'
  });

  useEffect(() => {
    const metaKeywords = document.querySelector('meta[name="keywords"]') || document.createElement('meta');
    metaKeywords.name = 'keywords';
    metaKeywords.content = 'batería muerta coche, batería agotada síntomas, batería coche no arranca, cuando cambiar batería coche, batería descargada o muerta';
    document.head.appendChild(metaKeywords);

    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Cómo Saber si la Batería del Coche Está Muerta",
      "description": "Guía para identificar si la batería de tu coche está muerta o descargada",
      "author": { "@type": "Organization", "name": "ArrancaYa BCN" },
      "datePublished": "2026-01-20",
      "dateModified": "2026-01-20"
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'page_view',
      'page_title': 'Cómo Saber Batería Muerta',
      'content_group1': 'Blog'
    });

    return () => {
      document.head.removeChild(script);
      if (metaKeywords.parentNode) document.head.removeChild(metaKeywords);
    };
  }, []);

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Navigation */}
      <div className="bg-obsidian/90 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link 
            to="/blog" 
            className="inline-flex items-center text-neon hover:text-neon/80 font-medium transition-colors group"
            data-testid="back-to-blog-link"
          >
            <ArrowLeft className="h-4 w-4 mr-2 transition-transform group-hover:-translate-x-1" />
            Volver al Blog
          </Link>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <header className="mb-12">
          <div className="flex items-center space-x-4 text-sm text-white/60 mb-6">
            <span className="bg-stark/20 text-stark px-3 py-1 rounded-sm font-medium uppercase tracking-wider text-xs">Diagnóstico</span>
            <span>•</span>
            <span>20 de enero, 2026</span>
            <span>•</span>
            <span>5 min de lectura</span>
          </div>
          
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-white mb-8 tracking-tighter leading-tight">
            ¿Batería Muerta o
            <span className="text-stark"> Solo Descargada?</span>
          </h1>
          
          <div className="bg-white/5 border border-white/10 rounded-sm p-8">
            <p className="text-xl text-white/80 leading-relaxed">
              <strong className="text-white">La diferencia es crucial:</strong> una batería descargada se puede arrancar y recargar. 
              Una batería muerta necesita ser reemplazada. Te enseñamos a distinguirlas.
            </p>
          </div>
        </header>

        {/* Content */}
        <div className="space-y-12">
          
          {/* Síntomas batería descargada */}
          <section className="bg-neon/10 border border-neon/30 rounded-sm p-8">
            <h2 className="font-heading text-2xl font-bold text-neon mb-6 flex items-center">
              <CheckCircle className="h-6 w-6 mr-3" />
              Síntomas de Batería DESCARGADA (se puede arrancar)
            </h2>
            
            <div className="space-y-4">
              {[
                { symptom: "Luces del dashboard se encienden pero débiles", desc: "El cuadro muestra luces tenues, pero hay señales de vida." },
                { symptom: "El motor intenta arrancar pero no tiene fuerza", desc: "Oyes 'clic clic clic' o el motor gira lento." },
                { symptom: "Las luces se apagan al girar la llave", desc: "Al intentar arrancar, todo se apaga por falta de potencia." },
                { symptom: "Funcionaba bien ayer", desc: "Dejaste las luces encendidas o hace mucho frío." },
                { symptom: "Los accesorios funcionan débilmente", desc: "Radio, ventanas eléctricas van lentas pero funcionan." }
              ].map((item, idx) => (
                <div key={idx} className="flex items-start space-x-4 bg-obsidian border border-neon/20 rounded-sm p-4">
                  <div className="bg-neon text-obsidian rounded-sm w-6 h-6 flex items-center justify-center font-bold flex-shrink-0 text-sm">
                    ✓
                  </div>
                  <div>
                    <h4 className="font-bold text-white">{item.symptom}</h4>
                    <p className="text-white/60 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-6 bg-neon/20 border border-neon rounded-sm p-4">
              <p className="text-neon font-bold text-center">
                ✅ BUENAS NOTICIAS: Podemos arrancarte en 30 minutos
              </p>
            </div>
          </section>

          {/* Síntomas batería muerta */}
          <section className="bg-stark/10 border border-stark/30 rounded-sm p-8">
            <h2 className="font-heading text-2xl font-bold text-stark mb-6 flex items-center">
              <XCircle className="h-6 w-6 mr-3" />
              Síntomas de Batería MUERTA (necesita cambio)
            </h2>
            
            <div className="space-y-4">
              {[
                { symptom: "Absolutamente nada se enciende", desc: "Ni luces, ni dashboard, ni radio. Silencio total." },
                { symptom: "La batería tiene más de 5 años", desc: "Vida útil típica: 4-6 años. Después, muere gradualmente." },
                { symptom: "Se descarga constantemente", desc: "La recargas y en 1-2 días vuelve a fallar." },
                { symptom: "Olor a huevos podridos", desc: "Indica fuga de ácido. ¡No toques la batería!" },
                { symptom: "Caja hinchada o deformada", desc: "La batería está dañada internamente. Peligro." },
                { symptom: "Terminales con mucha corrosión", desc: "Polvo blanco/verde abundante. Señal de degradación." }
              ].map((item, idx) => (
                <div key={idx} className="flex items-start space-x-4 bg-obsidian border border-stark/20 rounded-sm p-4">
                  <div className="bg-stark text-white rounded-sm w-6 h-6 flex items-center justify-center font-bold flex-shrink-0 text-sm">
                    ✗
                  </div>
                  <div>
                    <h4 className="font-bold text-white">{item.symptom}</h4>
                    <p className="text-white/60 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-6 bg-stark/20 border border-stark rounded-sm p-4">
              <p className="text-stark font-bold text-center">
                ⚠️ Necesitarás cambiar la batería. Podemos diagnosticarlo al llegar.
              </p>
            </div>
          </section>

          {/* Test rápido */}
          <section className="bg-white/5 border border-white/10 rounded-sm p-8">
            <h2 className="font-heading text-2xl font-bold text-white mb-6 flex items-center">
              <Battery className="h-6 w-6 text-neon mr-3" />
              Test Rápido: ¿Muerta o Descargada?
            </h2>
            
            <div className="bg-obsidian border border-white/10 rounded-sm p-6">
              <ol className="space-y-4 text-white/80">
                <li className="flex items-start">
                  <span className="bg-white/10 rounded-sm w-6 h-6 flex items-center justify-center mr-3 text-sm font-bold text-white flex-shrink-0">1</span>
                  <span><strong className="text-white">Gira la llave</strong> (o pulsa el botón de arranque)</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-white/10 rounded-sm w-6 h-6 flex items-center justify-center mr-3 text-sm font-bold text-white flex-shrink-0">2</span>
                  <span><strong className="text-white">¿Oyes algo?</strong> Clic, zumbido, o el motor girando lento = probablemente descargada</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-white/10 rounded-sm w-6 h-6 flex items-center justify-center mr-3 text-sm font-bold text-white flex-shrink-0">3</span>
                  <span><strong className="text-white">¿Silencio total?</strong> Ni un sonido = puede ser la batería muerta o un fusible</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-white/10 rounded-sm w-6 h-6 flex items-center justify-center mr-3 text-sm font-bold text-white flex-shrink-0">4</span>
                  <span><strong className="text-white">Enciende las luces interiores</strong> – si funcionan débilmente, es descarga</span>
                </li>
              </ol>
            </div>
          </section>

          {/* Qué hacer */}
          <section className="bg-white/5 border border-white/10 rounded-sm p-8">
            <h2 className="font-heading text-2xl font-bold text-white mb-6 flex items-center">
              <Info className="h-6 w-6 text-neon mr-3" />
              ¿Qué Hacer en Cada Caso?
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-neon/10 border border-neon/30 rounded-sm p-6">
                <h3 className="font-heading text-lg font-bold text-neon mb-4">Si está DESCARGADA:</h3>
                <ul className="text-white/80 space-y-2 text-sm">
                  <li className="flex items-start">
                    <span className="text-neon mr-2">1.</span>
                    Llámanos al 613 076 099
                  </li>
                  <li className="flex items-start">
                    <span className="text-neon mr-2">2.</span>
                    Llegamos en 30 min con batería portátil
                  </li>
                  <li className="flex items-start">
                    <span className="text-neon mr-2">3.</span>
                    Arrancamos tu coche (69€/99€)
                  </li>
                  <li className="flex items-start">
                    <span className="text-neon mr-2">4.</span>
                    Conduce 30 min para recargar
                  </li>
                </ul>
              </div>
              
              <div className="bg-stark/10 border border-stark/30 rounded-sm p-6">
                <h3 className="font-heading text-lg font-bold text-stark mb-4">Si está MUERTA:</h3>
                <ul className="text-white/80 space-y-2 text-sm">
                  <li className="flex items-start">
                    <span className="text-stark mr-2">1.</span>
                    Llámanos igualmente
                  </li>
                  <li className="flex items-start">
                    <span className="text-stark mr-2">2.</span>
                    Intentamos arrancar primero
                  </li>
                  <li className="flex items-start">
                    <span className="text-stark mr-2">3.</span>
                    Si no funciona, no pagas
                  </li>
                  <li className="flex items-start">
                    <span className="text-stark mr-2">4.</span>
                    Te recomendamos un taller cercano
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Prevención */}
          <section className="bg-white/5 border border-white/10 rounded-sm p-8">
            <h2 className="font-heading text-2xl font-bold text-white mb-6">
              Prevenir es Mejor que Curar
            </h2>
            
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { title: "Revisa cada 2 años", desc: "Haz un test de batería en tu taller habitual." },
                { title: "Cuidado con el frío", desc: "En invierno las baterías sufren más." },
                { title: "No dejes luces encendidas", desc: "El error más común de descarga." }
              ].map((tip, idx) => (
                <div key={idx} className="bg-obsidian border border-white/10 rounded-sm p-4 text-center">
                  <h4 className="font-bold text-white mb-2">{tip.title}</h4>
                  <p className="text-white/60 text-sm">{tip.desc}</p>
                </div>
              ))}
            </div>
          </section>

        </div>

        {/* CTA */}
        <div className="bg-neon text-obsidian rounded-sm p-8 mt-12">
          <div className="text-center">
            <h2 className="font-heading text-2xl font-black mb-4">¿Tu Coche No Arranca?</h2>
            <p className="text-obsidian/80 mb-6 max-w-xl mx-auto">
              No pierdas tiempo adivinando. Llámanos y te diagnosticamos en el momento. 
              Si no arranca, no pagas.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/')}
                className="bg-obsidian hover:bg-obsidian/90 text-white px-8 py-3 rounded-sm font-bold"
                data-testid="cta-whatsapp-btn"
              >
                <Zap className="h-5 w-5 mr-2" />
                Solicitar Diagnóstico
              </Button>
              <Button
                onClick={() => window.open('tel:+34613076099')}
                variant="outline"
                className="border-2 border-obsidian text-obsidian hover:bg-obsidian hover:text-white px-8 py-3 rounded-sm font-bold"
                data-testid="cta-phone-btn"
              >
                <Phone className="h-5 w-5 mr-2" />
                613 076 099
              </Button>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};

export default ComoSaberBateriaMuerta;
